import { useState, useCallback } from 'react';

import { pullActivity } from '../api';

export type UsePullActivityResult = {
  pullActivityFromQueue: () => Promise<number>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
};

/**
 * Hook for pulling activities from the queue.
 * Returns the count of activities assigned (0 if queue was empty).
 * Calls onSuccess callback when activities are successfully assigned.
 */
const usePullActivity = (onSuccess?: () => void): UsePullActivityResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pullActivityFromQueue = useCallback(async(): Promise<number> => {
    setIsLoading(true);
    setError(null);
    try {
      const count = await pullActivity();
      if (count > 0) {
        onSuccess?.();
      }
      return count;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to get activity';
      setError(message);
      return 0;
    } finally {
      setIsLoading(false);
    }
  }, [onSuccess]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { pullActivityFromQueue, isLoading, error, clearError };
};

export default usePullActivity;
